/**
 * Shared ESM module loader for ArcLog OS surfaces (apps, widgets).
 *
 * Dynamically imports a built /modules/<id>/index.js. Tries a native dynamic
 * import first; if that fails (e.g. some COEP/MIME edge cases), falls back to
 * fetching the source same-origin and importing it via a Blob URL.
 *
 * Used by AppHost and WidgetHost so every surface resolves modules identically.
 */
type ModuleShape = Record<string, unknown> & {
  default?: Record<string, unknown>;
};

export async function importModuleEsm<T = ModuleShape>(url: string): Promise<T> {
  try {
    return (await import(/* @vite-ignore */ url)) as T;
  } catch (primary) {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) {
      throw new Error(
        `Failed to fetch module ${url} (${res.status}). Native import error: ${String(primary)}`
      );
    }
    const code = await res.text();
    const blob = new Blob([code], { type: "text/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    try {
      return (await import(/* @vite-ignore */ blobUrl)) as T;
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
    }
  }
}
